import React, { createContext, useContext, useReducer, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import API_URL from "../config/Backendurl";

const AuthContext = createContext();

// Initialize axios defaults
axios.defaults.baseURL = `${API_URL}/api`;

// Note: token will be read inside the provider to ensure up-to-date value

const authReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN_START":
      return { ...state, loading: true, error: null };
    case "LOGIN_SUCCESS":
      localStorage.setItem("token", action.payload.token);
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${action.payload.token}`;
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: action.payload.user,
        loginMethod: action.payload.loginMethod || (action.payload.user?.email ? 'email' : 'wallet'),
        error: null,
      };
    case "LOGIN_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "LOGOUT":
      localStorage.removeItem("token");
      delete axios.defaults.headers.common["Authorization"];
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loginMethod: null,
        loading: false,
        error: null,
      };
    case "UPDATE_USER":
      return { ...state, user: action.payload };
    case "SET_THEME":
      const newTheme = action.payload;
      document.documentElement.classList.toggle("dark", newTheme === "dark");
      localStorage.setItem("theme", newTheme);
      return { ...state, theme: newTheme };
    case "CHECK_AUTH":
      return { ...state, loading: false };
    case "SET_LOGIN_METHOD":
      return { ...state, loginMethod: action.payload };
    default:
      return state;
  }
};

const initialState = {
  isAuthenticated: !!localStorage.getItem("token"),
  user: null,
  loginMethod: null,
  loading: true,
  error: null,
  theme: localStorage.getItem("theme") || "light",
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set initial theme
  useEffect(() => {
    document.documentElement.classList.toggle("dark", state.theme === "dark");
  }, [state.theme]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        axios.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${storedToken}`;
        try {
          const response = await axios.get("/auth/me");
          dispatch({
            type: "LOGIN_SUCCESS",
            payload: {
              token: storedToken,
              user: response.data.user,
              loginMethod: response.data.loginMethod || (response.data.user?.email ? 'email' : 'wallet'),
            },
          });
        } catch (error) {
          localStorage.removeItem("token");
          delete axios.defaults.headers.common["Authorization"];
          dispatch({ type: "LOGOUT" });
        }
      } else {
        dispatch({ type: "CHECK_AUTH" });
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials) => {
    // Drop expired/stale JWT so login is never blocked by an old Bearer token
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];

    dispatch({ type: "LOGIN_START" });
    try {
      const response = await axios.post("/auth/login", credentials);
      dispatch({
        type: "LOGIN_SUCCESS",
        payload: response.data,
      });
      toast.success("Login successful!");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || "Login failed";
      dispatch({ type: "LOGIN_ERROR", payload: message });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    dispatch({ type: "LOGIN_START" });
    try {
      // Determine which endpoint to use based on the data
      // If walletAddress is present, use register-wallet, otherwise use register-email
      const endpoint = userData.walletAddress ? "/auth/register-wallet" : "/auth/register-email";
      
      const response = await axios.post(endpoint, userData);
      dispatch({
        type: "LOGIN_SUCCESS",
        payload: response.data,
      });
      toast.success("Registration successful!");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || "Registration failed";
      dispatch({ type: "LOGIN_ERROR", payload: message });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    dispatch({ type: "LOGOUT" });
    toast.success("Logged out successfully");
    // Refresh the page after logout to ensure clean state
    setTimeout(() => {
      window.location.href = '/';
    }, 100);
  };

  const updateUser = (userData) => {
    dispatch({ type: "UPDATE_USER", payload: userData });
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get("/auth/me");
      dispatch({
        type: "LOGIN_SUCCESS",
        payload: {
          token: localStorage.getItem("token"),
          user: response.data.user,
          loginMethod: response.data.loginMethod || (response.data.user?.email ? 'email' : 'wallet'),
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };

  const switchRole = async (navigate = null, queryClient = null) => {
    try {
      const response = await axios.post("/users/switch-role");
      const updatedUser = { ...state.user, role: response.data.role };
      dispatch({ type: "UPDATE_USER", payload: updatedUser });
      toast.success(`Switched to ${response.data.role}`);
      
      // Invalidate all queries to refetch data with new role
      if (queryClient) {
        await queryClient.invalidateQueries();
      }
      
      // Redirect to dashboard if navigate function is provided
      if (navigate) {
        navigate('/dashboard');
        // Small delay to ensure queries are invalidated before navigation
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
      
      return { success: true, role: response.data.role };
    } catch (error) {
      const message = error.response?.data?.error || "Failed to switch role";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const toggleTheme = () => {
    const newTheme = state.theme === "light" ? "dark" : "light";
    dispatch({ type: "SET_THEME", payload: newTheme });
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    switchRole,
    toggleTheme,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
